<script lang="ts">
	import { get } from 'svelte/store';
	import {
		Alert,
		SelectCard,
		CardGrid,
		Toggle,
		Slider,
		Token,
		Text,
	} from '@Shelly/shelly-design-system';
	import {
		celsius_to_fahrenheit,
		fahrenheit_to_celsius,
		real_id,
	} from 'src/helpers/interface_helpers';
	import { createEventDispatcher } from 'svelte';
	import { capFirstChar } from 'src/helpers/utilities';
	import { format } from 'src/services/i18n';
	import TrvControlWithWheelV2 from './TRVControlWithWheelV2.svelte';
	import { getDeviceStatus } from 'src/stores/device-runtime/status-selectors';
	import { devices } from 'src/stores/lists/devices';
	import BLUTRVLocalActionAlerts from 'src/components/DevicesSettings/LocalActions/BLUTRVLocalActionAlerts.svelte';
	import { getWorkingModeIcon } from 'src/helpers/device_data_helpers';
	import { showNewLayout } from 'src/stores/toDelete/new_layout';

	export let actions = [], //both options - select percent level or change rooler/cover state
		active_actions = [], // active action must be the one of both provided options
		sceneIsValid = false,
		trvMinValue = 4,
		deviceId: string;

	const dispatch = createEventDispatcher();
	const optionsToBeHide = ['boost'];

	let realDeviceId: string;
	let initialDeviceId: string; //if initialDeviceId is the first loaded device, lately I will use it to check if the deviceId is changed when going back in "device" tab and choose another device
	let deviceChannel: number;
	let deviceStatus: any;
	let deviceTargetTemp: number;
	let positionValue: number;

	// Get favorites from device
	$: device = $devices[deviceId];
	$: valve_favorites = device?.valve_fav_positions || [];
	$: temp_fav_positions = device?.temp_fav_positions || [];
	$: active_temp_favorites = temp_fav_positions.filter((pos) => pos.active);

	if (import.meta.env.DEV) {
		console.log('%c ActionsOnTRVV2', 'color: orange', actions);
		console.log('%c Active ActionsOnTRVV2', 'color: orange', active_actions);
	}

	const getDeviceTargetTemp = (status: any, channel: number): number => {
		// Ignore if current active action is not temperature
		const defaultValue = 18;
		if (/^(target_temp|trv_target_temperature)$/.test(active_actions?.[0]?.name)) {
			return active_actions?.[0]?.value ?? defaultValue;
		} else {
			const bleTemp = status?.[`blutrv_rstatus:${channel}`]?.status?.['trv:0']?.current_C;
			if (typeof bleTemp !== 'undefined') return bleTemp;
			const thermostat = status?.thermostats?.[0];
			if (thermostat?.tmp?.units === 'C') return thermostat.tmp.value;
			if (thermostat?.tmp?.value) return fahrenheit_to_celsius(thermostat.tmp.value);
		}
		return defaultValue;
	};

	const getDeviceTargetPos = (): number => {
		const defaultValue = 50;
		if (active_actions?.[0]?.name === 'valve_percent_position') {
			return active_actions?.[0]?.value ?? defaultValue;
		}
		return defaultValue;
	};

	$: if (initialDeviceId !== deviceId) {
		initialDeviceId = deviceId;
		realDeviceId = deviceId.split('_')[0];
		deviceChannel = Number(deviceId.split('_')[1]) - 2000;
		deviceTargetTemp = getDeviceTargetTemp($statusStore, deviceChannel);
		positionValue = getDeviceTargetPos();
	}

	$: statusStore = getDeviceStatus(real_id(realDeviceId));

	$: selected = active_actions?.[0]?.name || undefined;

	const change_active_act_on_toggle = (actionName, e) => {
		active_actions[0] = {
			name: actionName,
			value: handleActionValueOnToggle(actionName),
		};
		if (!e.target.checked) active_actions = [];
		sceneIsValid = active_actions[0]?.value;
		dispatch('change_active', { new_active: active_actions });
	};

	const change_active_act_value = (act, val) => {
		if (val || val === 0) {
			active_actions[0] = { name: act.id, value: val };
			sceneIsValid = true;
			dispatch('change_active', { new_active: active_actions });
		} else {
			sceneIsValid = false;
		}
	};

	const handle_temp_change = (e, act) => {
		let { value, units } = e.detail;
		if (units === 'F') value = fahrenheit_to_celsius(value);
		change_active_act_value(act, value);
	};

	const handle_pos_changes = (e, act) => {
		let { value } = e.detail;
		change_active_act_value(act, value);
	};

	const _format_value = (act, val) => {
		switch (act.id) {
			case 'boost':
				return $format('command_start_boost');
			case 'report_window_open':
				return val === 'on'
					? $format(`command_${act.i18tr}_open`)
					: $format(`command_${act.i18tr}_close`);
			case 'set_profile':
				return act?.profiles?.[Number(val) - 1] || 'Profile ' + val;
			default:
				return '';
		}
	};

	const calculateValue = ({ units = 'C', value = '' }) =>
		units === '°F' ? celsius_to_fahrenheit(Number(value)) : Number(value);

	const handleActionValueOnToggle = (actionName) => {
		switch (actionName) {
			case 'target_temp':
			case 'trv_target_temperature':
				return deviceTargetTemp;
			case 'boost':
				return 'on';
			case 'valve_percent_position':
				return positionValue;
			default:
				return '';
		}
	};

	const a11yProps = {
		ariaLabel: $format('label_adjust_value', { default: 'Adjust value' }),
		ariaLabelValue: null,
	};
</script>

{#if actions.length > 0}
	{#each actions as act, i}
		<Toggle
			checked={selected === act.id}
			on:click={(e) => change_active_act_on_toggle(act.id, e)}
			size="l"
			class="mb-s"
			id="toggle-ve4lrux"
		>
			{capFirstChar($format(act.title))}
		</Toggle>
		{#if act.id === 'report_window_open'}
			<Alert
				class="mb-m"
				heading={$format('alert_required_window_open_setting', {
					default: 'Required: Enable "Open window" device setting.',
				})}
				type="warning"
			/>
		{:else if act.id === 'target_temp' && act.units === '°F'}
			<Alert
				class="mb-m"
				heading={$format('required_firmware_alert', {
					values: { version: 'v2.1.9' },
				})}
				type="warning"
			/>
		{/if}

		<!-- 
        For TRV-Gen 1 (act.id === 'target_temp') and BLE-TRV (act.id === 'trv_target_temperature')
        we have different btnStep values (0.5 and 0.1 respectively in Celcius)
        and step values (0.5 and 1 respectively in Celcius)
        https://gitlab.allterco.net/Shelly/frontend/shelly-app-3/-/issues/1934
        -->
		{#if act.id === 'target_temp' || act.id === 'trv_target_temperature'}
			{#if act.id === 'trv_target_temperature'}
				<BLUTRVLocalActionAlerts
					class="mb-m"
					device={get(devices)[deviceId]}
					action="target"
					disabled={selected !== act.id}
				/>
			{/if}
			<TrvControlWithWheelV2
				value={calculateValue({ ...act, value: deviceTargetTemp })}
				min={trvMinValue}
				units={act.units === '°F' ? 'F' : 'C'}
				disabled={selected !== act.id}
				on:change={(e) => handle_temp_change(e, act)}
				step={act.id === 'trv_target_temperature' ? 0.5 : act.units === '°F' ? 1 : 0.5}
				btnStep={act.id === 'trv_target_temperature' ? 0.1 : act.units === '°F' ? 1 : 0.5}
			/>
			{#if active_temp_favorites.length > 0}
				<Text class="mt-s mb-s" type="h4">
					{$format('label_favorite_temperatures', {
						default: 'Favorite temperatures:',
					})}
				</Text>
				<div class="d-flex flex-wrap" style="gap: var(--spacing-xs);">
					{#each active_temp_favorites as favTemp}
						<Token
							id={`token-temp-fav-${favTemp.mode}`}
							size="m"
							title="{favTemp.value}°"
							icon={getWorkingModeIcon(favTemp.mode)}
							selected={deviceTargetTemp === favTemp.value && selected === act.id}
							disabled={selected !== act.id}
							on:click={() => {
								if (selected === act.id) {
									deviceTargetTemp = favTemp.value;
									change_active_act_value(act, favTemp.value);
								}
							}}
						/>
					{/each}
				</div>
			{/if}
		{:else if act.id === 'valve_percent_position'}
			<BLUTRVLocalActionAlerts
				class="mb-m"
				device={get(devices)[deviceId]}
				action="position"
				disabled={selected !== act.id}
			/>
			<Slider
				id="slider-7mq0nzk"
				{a11yProps}
				type="simple"
				value={positionValue}
				class="flex-1"
				disabled={selected !== act.id}
				on:value_change={(e) => handle_pos_changes(e, act)}
				title={$format('label_cover_slider_title', {
					values: { pos: positionValue },
					default: 'Selected: {pos}%, Range: 0% (Fully closed) - 100 (Fully open)',
				})}
			/>
			{#if valve_favorites.length > 0}
				<Text class="mt-s mb-s" type="h4">
					{$format('label_favorite_positions', {
						default: 'Favorite positions:',
					})}
				</Text>
				<div class="d-flex flex-wrap" style="gap: var(--spacing-xs);">
					{#each valve_favorites as favPos}
						<Token
							id={`token-valve-fav-${favPos}`}
							size="m"
							title="{favPos}%"
							icon="fas fa-star"
							selected={positionValue === favPos && selected === act.id}
							disabled={selected !== act.id}
							on:click={() => {
								if (selected === act.id) {
									positionValue = favPos;
									change_active_act_value(act, favPos);
								}
							}}
						/>
					{/each}
				</div>
			{/if}
		{:else if act.values?.length && !optionsToBeHide.includes(act.id)}
			<CardGrid sm={200} md={200} xl={200} noPaddingBottom>
				{#each act.values as val}
					<div class="d-flex">
						<SelectCard
							cardBgColor={$showNewLayout ? 'color-1' : undefined}
							class="width-full"
							type="horizontal"
							variant="radio"
							title={_format_value(act, val)}
							selected={active_actions?.[0]?.value === val &&
								active_actions?.[0]?.name === act.id}
							disabled={selected !== act.id}
							on:click={() => change_active_act_value(act, val)}
						/>
					</div>
				{/each}
			</CardGrid>
		{/if}

		{#if i < actions.length - 1}
			<hr class="mb-m mt-m" />
		{/if}
	{/each}
{/if}
